import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip as UDSTooltip, Typography } from '@telus-uds/components-web';

const positionMap = {
  auto: 'auto',
  left: 'left',
  right: 'right',
  top: 'above',
  bottom: 'below'
};

/**
 * Renders tooltip content: parses markdown-style links [text](url) and plain URLs
 * into clickable links with Typography; otherwise returns the string or node.
 */
function renderContent(value) {
  if (typeof value !== 'string') return value;

  const markdownRegex = /\[(.*?)\]\((.*?)\)/g;
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  const markdownMatches = Array.from(value.matchAll(markdownRegex));

  if (markdownMatches.length) {
    markdownMatches.forEach(match => {
      const [fullMatch, text, url] = match;
      if (match.index > lastIndex) {
        parts.push(value.substring(lastIndex, match.index));
      }
      parts.push(
        <a href={url} target="_blank" rel="noopener noreferrer" key={match.index}>
          <Typography>{text}</Typography>
        </a>
      );
      lastIndex = match.index + fullMatch.length;
    });
    if (lastIndex < value.length) {
      parts.push(value.substring(lastIndex));
    }
  } else {
    let lastPlainIndex = 0;
    const urlMatches = Array.from(value.matchAll(urlRegex));
    urlMatches.forEach(match => {
      const [url] = match;
      if (match.index > lastPlainIndex) {
        parts.push(value.substring(lastPlainIndex, match.index));
      }
      parts.push(
        <a href={url} target="_blank" rel="noopener noreferrer" key={match.index}>
          <Typography>{url}</Typography>
        </a>
      );
      lastPlainIndex = match.index + url.length;
    });
    if (lastPlainIndex < value.length) {
      parts.push(value.substring(lastPlainIndex));
    }
  }
  return parts.length ? parts : value;
}

/**
 * UDS Tooltip wrapper for webforms. Uses TELUS Universal Design System
 * tooltip (correct styling, shadow, arrow, trigger icon) with optional
 * link parsing for string content.
 */
const Tooltip = ({ children, direction = 'auto', width }) => {
  const position = positionMap[direction] ?? 'auto';
  const content = renderContent(children);
  const tokens = width ? { maxWidth: width } : undefined;

  return (
    <UDSTooltip
      content={content}
      position={position}
      inline
      tokens={tokens}
    />
  );
};

Tooltip.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  direction: PropTypes.oneOf(['auto', 'left', 'right', 'top', 'bottom']),
  width: PropTypes.string
};

export default Tooltip;
